import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import type { SupplierInvoiceView } from '@wodi/shared';
import { Button, LoadingBlock, Notice, PageHeader, Section, Tag } from '@wodi/ui';
import { useState } from 'react';

import { decideSupplierInvoice, supplierInvoiceQuery } from '../api/procurement.ts';
import { sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { formatInstant, formatIsoDate } from '../patients/format.ts';
import { LoadFailed, OfflineNote, ReasonDialog } from '../procurement/common.tsx';
import { canApproveInvoice, documentLabel, documentTag } from '../procurement/format.ts';
import { MatchTable } from '../procurement/match-table.tsx';
import { formatCents } from '../stock/format.ts';
import styles from '../stock/stock.module.css';
import { toasts } from '../toasts.ts';

// One supplier invoice with its three-way match (docs/plans/phase2-procurement.md §5.10).
// Approving one that doesn't match needs a reason (CLAUDE.md, domain rule 10).

const route = getRouteApi('/_app/procurement/invoices/$invoiceId');

type Decision = 'approve' | 'reject' | 'cancel';
const WORDS: Record<Decision, { confirm: string; toast: string }> = {
  approve: { confirm: 'Approve for payment', toast: 'Invoice approved' },
  reject: { confirm: 'Reject invoice', toast: 'Invoice rejected' },
  cancel: { confirm: 'Cancel invoice', toast: 'Invoice cancelled' },
};

export function SupplierInvoicePage() {
  const { invoiceId } = route.useParams();
  const invoice = useQuery(supplierInvoiceQuery(invoiceId));
  if (invoice.isPending) return <LoadingBlock label="Loading the invoice" rows={5} />;
  if (invoice.isError)
    return (
      <LoadFailed
        error={invoice.error}
        fallback="The supplier invoice couldn't be loaded."
        onRetry={() => void invoice.refetch()}
      />
    );
  return <InvoiceDetail invoice={invoice.data} />;
}

function InvoiceDetail({ invoice }: { invoice: SupplierInvoiceView }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const queryClient = useQueryClient();
  const [deciding, setDeciding] = useState<Decision | null>(null);
  const decide = useMutation({
    mutationFn: ({ decision, reason }: { decision: Decision; reason: string }) =>
      decideSupplierInvoice(invoice.id, decision, reason === '' ? null : reason),
    onSuccess: async (result, { decision }) => {
      queryClient.setQueryData(supplierInvoiceQuery(invoice.id).queryKey, result);
      await queryClient.invalidateQueries({ queryKey: ['procurement'] });
      toasts.show({ title: WORDS[decision].toast, description: result.number });
      setDeciding(null);
    },
  });
  const mine = session?.user.id === invoice.recordedBy.id;
  const waiting = invoice.status === 'awaiting_approval';
  return (
    <div className={styles.page}>
      <p className={styles.meta}>
        <Link to="/procurement/invoices">Back to supplier invoices</Link>
      </p>
      <PageHeader title={`Supplier invoice ${invoice.number}`} />
      <Section
        title={invoice.supplier.name}
        actions={<Tag state={documentTag(invoice.status)}>{documentLabel(invoice.status)}</Tag>}
      >
        <dl className={styles.facts}>
          <dt>Their invoice number</dt>
          <dd>
            <span className="wodi-id">{invoice.supplierReference}</span>
          </dd>
          <dt>LPO</dt>
          <dd>
            <Link to="/procurement/orders/$orderId" params={{ orderId: invoice.purchaseOrder.id }}>
              <span className="wodi-id">{invoice.purchaseOrder.number}</span>
            </Link>
          </dd>
          <dt>Invoice date</dt>
          <dd>{formatIsoDate(invoice.invoiceDate)}</dd>
          {invoice.dueOn === null ? null : (
            <>
              <dt>Due on</dt>
              <dd>{formatIsoDate(invoice.dueOn)}</dd>
            </>
          )}
          <dt>VAT as printed</dt>
          <dd className={styles.numbers}>{formatCents(invoice.vat.cents)}</dd>
          <dt>Total as printed</dt>
          <dd className={styles.numbers}>{formatCents(invoice.total.cents)}</dd>
          <dt>Recorded by</dt>
          <dd>
            {invoice.recordedBy.name}, {formatInstant(invoice.recordedAt)}
          </dd>
          {invoice.decisionReason === null && invoice.decidedBy === null ? null : (
            <>
              <dt>Decision</dt>
              <dd>
                {invoice.decidedBy?.name ?? invoice.recordedBy.name}
                {invoice.decisionReason === null ? '' : `: ${invoice.decisionReason}`}
              </dd>
            </>
          )}
        </dl>
      </Section>
      <Section
        title="Against the LPO and goods received"
        actions={
          <Tag state={invoice.match.matched ? 'done' : 'attention'}>
            {invoice.match.matched ? 'Matches' : 'Differences to explain'}
          </Tag>
        }
      >
        <p className={styles.meta}>
          {invoice.matchAsOf === 'now'
            ? 'As things stand now: goods received later change this.'
            : 'As the approver saw it when deciding.'}
        </p>
        <MatchTable match={invoice.match} />
        <div className={styles.actions}>
          {invoice.canDecide ? (
            <>
              <Button
                variant="primary"
                isDisabled={!online}
                onPress={() => {
                  setDeciding('approve');
                }}
              >
                Approve for payment
              </Button>
              <Button
                isDisabled={!online}
                onPress={() => {
                  setDeciding('reject');
                }}
              >
                Reject
              </Button>
            </>
          ) : null}
          {waiting && mine ? (
            <>
              <span className={styles.meta}>
                Another person with approval rights must approve this.
              </span>
              <Button
                variant="quiet"
                isDisabled={!online}
                onPress={() => {
                  setDeciding('cancel');
                }}
              >
                Cancel invoice
              </Button>
            </>
          ) : null}
        </div>
        <OfflineNote what="Deciding an invoice" />
        {waiting ? (
          <Notice tone="info">
            Supplier payments are not recorded in Wodi. Approving marks this invoice as fit to pay.
          </Notice>
        ) : null}
      </Section>
      {deciding === null ? null : (
        <ReasonDialog
          title={`${WORDS[deciding].confirm}: ${invoice.number}`}
          confirmLabel={WORDS[deciding].confirm}
          optional={deciding === 'approve' && canApproveInvoice(invoice.match, '')}
          description={
            deciding === 'approve' && !invoice.match.matched
              ? "This invoice doesn't match. Say why it should be paid anyway; at least five characters."
              : 'At least five characters. It is kept with the decision.'
          }
          isPending={decide.isPending}
          error={decide.error}
          onConfirm={(reason) => {
            decide.mutate({ decision: deciding, reason });
          }}
          onClose={() => {
            setDeciding(null);
            decide.reset();
          }}
        />
      )}
    </div>
  );
}
